import { useTranslation } from 'react-i18next'

import { getQaSelector } from '../../../utils/common'
import {
  DRAG_DROP_SORT_STRATEGY,
  DragDropProvider,
  DragDropSortableItem,
  DragDropSortableList,
} from '../../DragDrop'
import {
  FilesPreviewGallery,
  useFilePreviewGallery,
} from '../../FilesPreviewGallery'
import Text from '../../Text'

import {
  PHOTO_MAX_SIZE_MB,
  QA_NAME_PHOTO_GALLERY,
  QA_NAME_PHOTO_LIMITS,
} from './RentalPortalPhotoGallery.constants'
import {
  useRentalPortalPhotoGalleryDelete,
  useRentalPortalPhotoGalleryUpload,
} from './RentalPortalPhotoGallery.hooks'
import {
  getPhotoCoverIndex,
  getPhotoReorderState,
  getPhotosWithCover,
  getReorderedPhotos,
  toPhotoPreviewFiles,
} from './RentalPortalPhotoGallery.utils'
import { RentalPortalPhotoGalleryDeleteConfirmation } from './RentalPortalPhotoGalleryDeleteConfirmation'
import { RentalPortalPhotoGalleryDropzone } from './RentalPortalPhotoGalleryDropzone'
import { RentalPortalPhotoGalleryTile } from './RentalPortalPhotoGalleryTile'

import * as styles from './RentalPortalPhotoGallery.module.css'

function RentalPortalPhotoGallery({
  disabled,
  disabledReason,
  photos,
  onChange,
  onDeletePhoto,
  onUploadingFilesChange,
}) {
  const { t } = useTranslation()

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({ files: photos })

  const {
    filesError,
    filesUploading,
    handleErrorUpload,
    handleExcludeFile,
    handleFinishUpload,
    handleGetUploadedFiles,
    handleRemoveUploadTile,
    handleRetryPhoto,
    handleStartUpload,
    setPhotos,
  } = useRentalPortalPhotoGalleryUpload({
    photos: photos,
    onChange,
    onUploadingFilesChange,
  })

  const {
    deletingPhoto,
    handleCancelDelete,
    handleConfirmDelete,
    handleDeletePhoto,
  } = useRentalPortalPhotoGalleryDelete({
    photos: photos,
    onDeletePhoto,
    onExcludeFile: handleExcludeFile,
    onRemoveUploadTile: handleRemoveUploadTile,
  })

  function handleDragEnd({ active, over }) {
    if (over && active.id !== over.id) {
      setPhotos(getReorderedPhotos(photos, active.id, over.id))
    }
  }

  function handleSetCover(photoId) {
    setPhotos(getPhotosWithCover(photos, photoId))
  }

  const { hasReorder, isReorderEnabled, reorderDisabledReason } =
    getPhotoReorderState({
      disabled,
      disabledReason,
      hasUploading: filesUploading.length > 0,
      photosCount: photos.length,
      t,
    })

  const coverIndex = getPhotoCoverIndex(photos)

  function renderStateTile(file) {
    return (
      <div className={styles.gridItem} key={file.id}>
        <RentalPortalPhotoGalleryTile
          disabled={disabled}
          disabledReason={disabledReason}
          photo={file}
          onDelete={handleDeletePhoto}
          onRetry={handleRetryPhoto}
        />
      </div>
    )
  }

  return (
    <div
      {...getQaSelector({ value: QA_NAME_PHOTO_GALLERY })}
      className={styles.RentalPortalPhotoGallery}
    >
      <DragDropProvider onDragEnd={handleDragEnd}>
        <DragDropSortableList
          className={styles.grid}
          items={photos.map(photo => photo.id)}
          strategy={DRAG_DROP_SORT_STRATEGY.grid}
        >
          {photos.map((photo, index) => (
            <DragDropSortableItem
              className={styles.gridItem}
              id={photo.id}
              key={photo.id}
              sortableProps={{ disabled: !isReorderEnabled }}
            >
              <RentalPortalPhotoGalleryTile
                disabled={disabled}
                disabledReason={disabledReason}
                hasReorder={hasReorder}
                isCover={index === coverIndex}
                isReorderDisabled={hasReorder && !isReorderEnabled}
                photo={photo}
                position={index + 1}
                reorderDisabledReason={reorderDisabledReason}
                onClick={handleClickFile}
                onDelete={handleDeletePhoto}
                onSetCover={handleSetCover}
              />
            </DragDropSortableItem>
          ))}
          {filesUploading.map(renderStateTile)}
          {filesError.map(renderStateTile)}

          <RentalPortalPhotoGalleryDropzone
            disabled={disabled}
            disabledReason={disabledReason}
            onErrorUpload={handleErrorUpload}
            onFinishUpload={handleFinishUpload}
            onGetUploadedFiles={handleGetUploadedFiles}
            onStartUpload={handleStartUpload}
          />
        </DragDropSortableList>
      </DragDropProvider>

      <Text isSecondary className={styles.limits} qaName={QA_NAME_PHOTO_LIMITS}>
        {t('RentalPortal.ListingPhotoLimitsKey', {
          size: PHOTO_MAX_SIZE_MB,
        })}
      </Text>

      {deletingPhoto ? (
        <RentalPortalPhotoGalleryDeleteConfirmation
          photo={deletingPhoto}
          photos={photos}
          onCancel={handleCancelDelete}
          onSubmit={handleConfirmDelete}
        />
      ) : null}

      <FilesPreviewGallery
        files={toPhotoPreviewFiles(photos)}
        initActive={activeFileIndex}
        onClose={handleCloseFilePreview}
      />
    </div>
  )
}

export default RentalPortalPhotoGallery
